import React from 'react'
import { rulerFraction } from '@/lib/market'
import { cn } from '@/lib/utils'
import type { MarketPriceDomain } from '@/types/market'

/**
 * A one-line picture of a night on the page's shared price ruler: the compset's band, its median
 * as a tick, and your price as a teal dot. Decorative (`aria-hidden`): every fact in it is also
 * printed beside it.
 */
export default function RangeTrack({
  min,
  median,
  max,
  price,
  domain,
  className,
}: {
  min: number | null
  median: number | null
  max: number | null
  price: number | null
  domain: Pick<MarketPriceDomain, 'min' | 'max'>
  className?: string
}) {
  const lo = min == null ? null : rulerFraction(min, domain)
  const hi = max == null ? null : rulerFraction(max, domain)
  return (
    <div aria-hidden="true" className={cn('relative h-4 w-full', className)}>
      <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-border" />
      {lo != null && hi != null && (
        <div
          className="absolute top-1/2 h-2 -translate-y-1/2 rounded-full bg-slate-300/70 ring-1 ring-inset ring-slate-400/40"
          style={{ left: `${lo}%`, width: `max(${hi - lo}%, 2px)` }}
        />
      )}
      {median != null && (
        <div
          className="absolute top-1/2 h-3 w-px -translate-y-1/2 bg-slate-500"
          style={{ left: `${rulerFraction(median, domain)}%` }}
        />
      )}
      {price != null && (
        <div
          className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary ring-2 ring-white"
          style={{ left: `${rulerFraction(price, domain)}%` }}
        />
      )}
    </div>
  )
}
